import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { posthog } from '@/lib/posthog';
import { api, type MissingModelDetails } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  newPartialJsonState,
  appendDelta,
  type ParserState,
} from '@/lib/partial-json';
import { usePaymentRequiredContext } from '@/contexts/PaymentRequiredContext';
import { triggerProtocolReload } from '@/lib/protocol-reload';
import type {
  SSEEvent,
  ToolName,
  PlanStep,
  SummarySection,
} from '@arcanum-hq/shared-types';

/**
 * Hedge phrases the LLM uses to narrate a retry without acknowledging the
 * previous tool failure. Source list from the Atlanta Deal6 transcript
 * (agent-tool-reliability spec section 5). Detected at the START of an AI
 * token-stream message — we catch the first hedge of the message body and
 * log it ONCE per message via `console.log('[metrics.tool_retry]', ...)`.
 *
 * Console-only telemetry — NO production analytics pipeline, NO in-app UI
 * surface, NO localStorage. The triage rule of thumb (>2 hedges in a single
 * conversation = a tool is regressing) is a HUMAN-side observation.
 *
 * Extend by adding alternations. Exported so the spec can import the same
 * regex without duplication.
 */
export const HEDGE_PHRASE_REGEX =
  /^\s*(let me try|let me fix|let me include|let me set|let me check|let me run)\b/i;

export type StreamingStatus =
  | 'idle'
  | 'connecting'
  | 'streaming'
  | 'done'
  | 'error'
  // prompt-langgraph-optimization Phase 5 Session 2 (2026-05-03) — terminating
  // status when the API SSE controller pre-empts on a `security_block` event.
  | 'security_blocked'
  // agent-ux-optimization Phase 2 Session 1 — terminal status when the user
  // (or server) aborts the run. Distinct from `'idle'` so the UI can render
  // an aborted-cosmetic indicator on the partial message; distinct from
  // `'error'` because no actual error occurred — the run was deliberately
  // terminated.
  | 'aborted';

/**
 * Per-message security redaction span. Populated by the `security_redact`
 * SSE event handler (Phase 5 Session 2).
 */
export interface SecurityRedactedSpan {
  category:
    | 'system_prompt'
    | 'tool_name'
    | 'cross_user_id'
    | 'methodology'
    | 'calibration_band'
    | 'semantic_rule';
  stage: 'token' | 'semantic';
  redactedSpan: string;
  replacement: string;
  timestamp: number;
}

/**
 * Tool call state machine — directive §3 + Phase 2 Session 1.
 *
 * One entry per `tool_call_id` in `StreamingMessage.toolCallStates`. Live
 * stream populates IDs from real Anthropic events; hydration synthesizes
 * `legacy-${messageId}-${index}` IDs for past turns. Renderers consume
 * `state.phase` to dispatch among 5 visual phases (skeleton / in-progress /
 * complete / errored / aborted).
 *
 * The existing `tool_start` / `tool_result` / `tool_error` SSE variants do
 * NOT carry `tool_call_id` (Phase 1 kept the existing 8 variants verbatim),
 * so the reducer transitions the most-recent matching entry by tool name +
 * pending phase. Today's agent runs sequentially — at most one tool of a
 * given name in flight — so this is unambiguous. Cross-instance parallel
 * tool calls would require either an explicit ID on these variants or an
 * additional disambiguator; not in scope for v1.
 */
export type ToolCallState =
  | {
      phase: 'input_streaming';
      tool: ToolName;
      partialInput: object;
      rawJson: string;
    }
  | { phase: 'input_ready'; tool: ToolName; input: object }
  | {
      phase: 'executing';
      tool: ToolName;
      input: object;
      progress: ToolProgressEvent[];
    }
  | { phase: 'complete'; tool: ToolName; input: object; result: unknown }
  | {
      phase: 'errored';
      tool: ToolName;
      input: object | null;
      error: {
        code: string;
        message: string;
        details?: Record<string, unknown>;
      };
    }
  | { phase: 'aborted'; tool: ToolName; input: object | null };

export interface ToolProgressEvent {
  /** tool-specific lifecycle phase (e.g. 'cell_computed', 'tier_hit'). */
  phase: string;
  /** Validated against tool-progress.ts schemas at the consumer (Phase 4). */
  payload: Record<string, unknown>;
}

/**
 * Reasoning summary block — populated by the thinking_start/_delta/_stop
 * lifecycle. `<ReasoningPill>` (Session 4) reads this to render the live tick
 * during streaming and the static "Reasoned for X.Xs" form after stop.
 *
 * Empty `text` after `thinking_stop` (Opus 4.7 default behavior per directive
 * §9.2 when `display: 'summarized'` is NOT set) renders the explicit
 * `(no thinking content)` fallback in the pill.
 */
export interface ThinkingBlock {
  /** ms epoch — tick source for live duration. */
  startedAt: number;
  /** Populated on thinking_stop. null while live. */
  durationMs: number | null;
  /** Populated on thinking_stop if the event carries it. */
  tokenCount: number | null;
  /** Accumulated thinking_delta text. */
  text: string;
}

/**
 * Plan-then-execute state — populated by the propose_plan synthetic tool's
 * `plan_proposed` SSE event + ticked through by `plan_step_active` /
 * `plan_step_complete`.
 *
 * I3 invariant (design.md): tool-error mid-plan sets `erroredStepId` and the
 * visual evidence persists across subsequent step transitions — `plan_step_-
 * active` for a NEW step does NOT clear the prior errored marker.
 */
export interface PlanState {
  steps: PlanStep[];
  activeStepId: string | null;
  completedStepIds: Set<string>;
  erroredStepId: string | null;
}

export interface StreamingMessage {
  id: string;
  role: 'ai';
  content: string;
  // Existing fields — retained verbatim for hydration consumers + Phase 4/5
  // hardening. Session 4's <ChatThread> rewrite stops READING `toolResults` /
  // `toolErrors` for rendering (it iterates `toolCallStates` instead) but
  // continues to populate them so any non-migrated consumer keeps working.
  toolResults: Array<{ tool: ToolName; result: unknown; timestamp: number }>;
  toolErrors: Array<{
    tool: ToolName;
    code: string;
    message: string;
    details?: Record<string, unknown>;
    timestamp: number;
  }>;
  redactedSpans: SecurityRedactedSpan[];
  /**
   * Derived from `toolCallStates`: the most-recently-inserted entry whose
   * phase is `'executing'`. Kept for back-compat with consumers that still
   * read this scalar (notably the existing ChatThread thinking-indicator
   * branch — Session 4 retires that read in the same diff that lands the
   * receipt-grouping iteration).
   */
  activeToolCall: ToolName | null;
  isStreaming: boolean;
  // New (Phase 2 Session 1):
  /** tool_call_id → state machine entry. New Map per state update for React. */
  toolCallStates: Map<string, ToolCallState>;
  thinking: ThinkingBlock | null;
  plan: PlanState | null;
  summary: { sections: SummarySection[] } | null;
  abortedReason: 'user' | 'timeout' | 'budget' | 'error' | null;
}

export interface SecurityBlockDetails {
  reason: string;
  signals: string[];
}

interface UseStreamingReturn {
  status: StreamingStatus;
  currentMessage: StreamingMessage | null;
  error: string | null;
  missingModel: MissingModelDetails | null;
  securityBlock: SecurityBlockDetails | null;
  /**
   * Phase 7 overload retry (2026-05-15). Populated while the API is retrying
   * an upstream `overloaded_error` between attempts. Web renders an inline
   * "Retrying… (attempt N/total)" indicator under the chat input. Cleared on
   * success, final error, or abort.
   */
  retryStatus: {
    attempt: number;
    totalAttempts: number;
    delayMs: number;
  } | null;
  /**
   * Friendly retriable error (currently UPSTREAM_OVERLOADED after exhausted
   * retries). Carries the original `(conversationId, content)` so the Retry
   * button in <ChatThread>'s retry card can re-fire without re-typing.
   */
  retriableError: {
    message: string;
    code: string;
    conversationId: string;
    content: string;
  } | null;
  /**
   * Re-fire `sendMessage(retriableError.conversationId, retriableError.content)`.
   * No-op when `retriableError` is null. Clears `retriableError` before the
   * re-send so the card unmounts cleanly.
   */
  retry: () => void;
  sendMessage: (conversationId: string, content: string) => void;
  /**
   * Dual-fire abort — directive §10.5 + Phase 2 Session 1. Calls the local
   * `AbortController.abort()` (terminates the SSE fetch) AND posts to the
   * Phase 1 server-side abort endpoint (terminates the LangGraph run so it
   * stops burning tokens). Optimistically transitions status to `'aborted'`
   * so the UI responds within the 500ms budget regardless of network
   * round-trip latency.
   */
  abort: () => Promise<void>;
  /**
   * Clear a terminal `error` (and reset `status` from `'error'` → `'idle'`) so
   * the SSE error banner can be dismissed without sending a new message.
   * builder-ux-overhaul FEATURE_END (2026-05-22) — the Dismiss button only
   * cleared the route-local `uiError`, leaving `streamingError` set so the
   * banner never went away (F09).
   */
  clearError: () => void;
}

// --- helpers ---

function findLastEntry<T extends ToolCallState>(
  states: Map<string, ToolCallState>,
  predicate: (state: ToolCallState) => state is T
): { id: string; state: T } | null {
  let last: { id: string; state: T } | null = null;
  for (const [id, state] of states) {
    if (predicate(state)) last = { id, state };
  }
  return last;
}

const isInputReady = (
  s: ToolCallState
): s is Extract<ToolCallState, { phase: 'input_ready' }> =>
  s.phase === 'input_ready';
const isExecuting = (
  s: ToolCallState
): s is Extract<ToolCallState, { phase: 'executing' }> =>
  s.phase === 'executing';
const isPendingTerminal = (
  s: ToolCallState
): s is Extract<
  ToolCallState,
  { phase: 'input_streaming' | 'input_ready' | 'executing' }
> =>
  s.phase === 'input_streaming' ||
  s.phase === 'input_ready' ||
  s.phase === 'executing';

function deriveActiveToolCall(
  states: Map<string, ToolCallState>
): ToolName | null {
  const entry = findLastEntry(states, isExecuting);
  return entry?.state.tool ?? null;
}

export function useStreaming(): UseStreamingReturn {
  const [status, setStatus] = useState<StreamingStatus>('idle');
  const [currentMessage, setCurrentMessage] = useState<StreamingMessage | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [missingModel, setMissingModel] = useState<MissingModelDetails | null>(
    null
  );
  const [securityBlock, setSecurityBlock] =
    useState<SecurityBlockDetails | null>(null);
  // Phase 7 overload retry (2026-05-15). When the API yields a `retry_status`
  // SSE event between in-stream retries, `retryStatus` populates with the
  // current attempt + delay so the UI can render "Retrying… (2/4)" under the
  // chat input. Cleared on success / final error / abort.
  const [retryStatus, setRetryStatus] = useState<{
    attempt: number;
    totalAttempts: number;
    delayMs: number;
  } | null>(null);
  // Friendly retriable-error state. Distinguished from `error` (generic
  // terminal) by carrying the original conversationId + content so the UI's
  // Retry button can re-fire `sendMessage(conversationId, content)` without
  // making the user re-type the prompt.
  const [retriableError, setRetriableError] = useState<{
    message: string;
    code: string;
    conversationId: string;
    content: string;
  } | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);
  const activeConversationIdRef = useRef<string | null>(null);
  // Last (conversationId, content) pair captured at sendMessage time —
  // consumed by the Retry button when an UPSTREAM_OVERLOADED error fires.
  const lastSendRef = useRef<{
    conversationId: string;
    content: string;
  } | null>(null);
  // Hedge-phrase detection state — reset per sendMessage (per-message-body firing).
  const hedgeDetectedRef = useRef(false);
  const lastToolRef = useRef<ToolName | null>(null);
  // Per-tool-call partial-JSON parser state. Reset per sendMessage; keyed by
  // tool_call_id so concurrent (or sequentially overlapping) calls don't
  // contaminate each other's stack/inString flags.
  const partialJsonStatesRef = useRef<Map<string, ParserState>>(new Map());
  const queryClient = useQueryClient();
  // signup-billing-mvp Phase 6 Session 3 (2026-05-13): cross-cutting
  // PAYMENT_REQUIRED signal. Producer-side wiring; banner reads via the
  // thinner useStreamingPaymentRequired() accessor.
  const { setPaymentRequired } = usePaymentRequiredContext();

  const sendMessage = useCallback(
    async (conversationId: string, content: string) => {
      setStatus('connecting');
      setError(null);
      setMissingModel(null);
      setSecurityBlock(null);
      setRetryStatus(null);
      setRetriableError(null);
      lastSendRef.current = { conversationId, content };
      // P6 Pattern B (research § 11 risk #3): clear stale 402 signal at
      // run-start so the banner reappears if the new attempt re-trips the gate.
      setPaymentRequired(null);
      hedgeDetectedRef.current = false;
      lastToolRef.current = null;
      partialJsonStatesRef.current = new Map();
      activeConversationIdRef.current = conversationId;

      const msgId = `msg-${Date.now()}`;
      const newMessage: StreamingMessage = {
        id: msgId,
        role: 'ai',
        content: '',
        toolResults: [],
        toolErrors: [],
        redactedSpans: [],
        activeToolCall: null,
        isStreaming: true,
        toolCallStates: new Map(),
        thinking: null,
        plan: null,
        summary: null,
        abortedReason: null,
      };
      setCurrentMessage(newMessage);

      // NEW Phase 3 § 3.4 — PostHog product event: user sent a message.
      posthog.capture('message_sent', { conversation_id: conversationId });

      const cleanup = await api.stream(
        `/api/conversations/${conversationId}/messages/stream`,
        { content },
        (event) => {
          const sse = event as SSEEvent;
          setStatus('streaming');

          switch (sse.type) {
            case 'token':
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const newContent = prev.content + sse.content;
                if (
                  !hedgeDetectedRef.current &&
                  newContent.trim().length >= 8 &&
                  HEDGE_PHRASE_REGEX.test(newContent)
                ) {
                  const match = newContent.match(HEDGE_PHRASE_REGEX);
                  if (match) {
                    hedgeDetectedRef.current = true;
                    console.log('[metrics.tool_retry]', {
                      conversationId,
                      messageId: prev.id,
                      hedgePhrase: match[1].toLowerCase(),
                      lastTool: lastToolRef.current,
                    });
                  }
                }
                return { ...prev, content: newContent };
              });
              break;

            // ---------- Reasoning lifecycle ----------

            case 'thinking_start':
              setCurrentMessage((prev) =>
                prev
                  ? {
                      ...prev,
                      thinking: {
                        startedAt: Date.now(),
                        durationMs: null,
                        tokenCount: null,
                        text: '',
                      },
                    }
                  : prev
              );
              break;

            case 'thinking_delta':
              setCurrentMessage((prev) =>
                prev?.thinking
                  ? {
                      ...prev,
                      thinking: {
                        ...prev.thinking,
                        text: prev.thinking.text + sse.text,
                      },
                    }
                  : prev
              );
              break;

            case 'thinking_stop':
              setCurrentMessage((prev) =>
                prev?.thinking
                  ? {
                      ...prev,
                      thinking: {
                        ...prev.thinking,
                        durationMs: sse.duration_ms,
                        tokenCount: sse.token_count ?? null,
                      },
                    }
                  : prev
              );
              break;

            // ---------- Tool input lifecycle ----------

            case 'tool_input_start': {
              const id = sse.tool_call_id;
              partialJsonStatesRef.current.set(id, newPartialJsonState());
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const next = new Map(prev.toolCallStates);
                next.set(id, {
                  phase: 'input_streaming',
                  tool: sse.tool,
                  partialInput: {},
                  rawJson: '',
                });
                return { ...prev, toolCallStates: next };
              });
              break;
            }

            case 'tool_input_delta': {
              const id = sse.tool_call_id;
              const parserState =
                partialJsonStatesRef.current.get(id) ?? newPartialJsonState();
              const { state: nextParser, parsed } = appendDelta(
                parserState,
                sse.partial_input_json
              );
              partialJsonStatesRef.current.set(id, nextParser);
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const existing = prev.toolCallStates.get(id);
                if (!existing || existing.phase !== 'input_streaming')
                  return prev;
                const next = new Map(prev.toolCallStates);
                next.set(id, {
                  phase: 'input_streaming',
                  tool: existing.tool,
                  partialInput: parsed,
                  rawJson: nextParser.rawJson,
                });
                return { ...prev, toolCallStates: next };
              });
              break;
            }

            case 'tool_input_ready': {
              const id = sse.tool_call_id;
              partialJsonStatesRef.current.delete(id);
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const existing = prev.toolCallStates.get(id);
                if (!existing) return prev;
                const next = new Map(prev.toolCallStates);
                next.set(id, {
                  phase: 'input_ready',
                  tool: existing.tool,
                  input: sse.input,
                });
                return { ...prev, toolCallStates: next };
              });
              break;
            }

            // ---------- Tool execution (existing 8 variants — kept verbatim shape) ----------

            case 'tool_start':
              // tool_start does NOT carry tool_call_id (Phase 1 kept the variant
              // verbatim per directive's "additive" rule). Match by tool name +
              // most-recent input_ready entry. Today's agent runs tools
              // sequentially so this is unambiguous; if multi-instance parallel
              // calls land in v2, the variant gains an explicit ID at that time.
              lastToolRef.current = sse.tool;
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const matched = findLastEntry(
                  prev.toolCallStates,
                  (s): s is Extract<ToolCallState, { phase: 'input_ready' }> =>
                    isInputReady(s) && s.tool === sse.tool
                );
                const next = new Map(prev.toolCallStates);
                if (matched) {
                  next.set(matched.id, {
                    phase: 'executing',
                    tool: matched.state.tool,
                    input: matched.state.input,
                    progress: [],
                  });
                } else {
                  // No prior input_ready (legacy stream without tool_input_*
                  // lifecycle, or schema drift). Synthesize a fallback ID so
                  // the receipt-grouping iteration still surfaces this call.
                  const fallbackId = `legacy-live-${sse.tool}-${Date.now()}`;
                  next.set(fallbackId, {
                    phase: 'executing',
                    tool: sse.tool,
                    input: {},
                    progress: [],
                  });
                }
                return {
                  ...prev,
                  toolCallStates: next,
                  activeToolCall: deriveActiveToolCall(next),
                };
              });
              break;

            case 'tool_progress': {
              const id = sse.tool_call_id;
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const existing = prev.toolCallStates.get(id);
                if (!existing || existing.phase !== 'executing') return prev;
                const next = new Map(prev.toolCallStates);
                next.set(id, {
                  ...existing,
                  progress: [
                    ...existing.progress,
                    { phase: sse.phase, payload: sse.payload },
                  ],
                });
                return { ...prev, toolCallStates: next };
              });
              break;
            }

            case 'tool_result':
              lastToolRef.current = sse.tool;
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const matched = findLastEntry(
                  prev.toolCallStates,
                  (s): s is Extract<ToolCallState, { phase: 'executing' }> =>
                    isExecuting(s) && s.tool === sse.tool
                );
                const next = new Map(prev.toolCallStates);
                if (matched) {
                  next.set(matched.id, {
                    phase: 'complete',
                    tool: matched.state.tool,
                    input: matched.state.input,
                    result: sse.result,
                  });
                } else {
                  // No prior executing entry — synthesize a fallback so the
                  // receipt-grouping iteration still has something to render.
                  const fallbackId = `legacy-live-${sse.tool}-${Date.now()}`;
                  next.set(fallbackId, {
                    phase: 'complete',
                    tool: sse.tool,
                    input: {},
                    result: sse.result,
                  });
                }
                return {
                  ...prev,
                  activeToolCall: deriveActiveToolCall(next),
                  toolResults: [
                    ...prev.toolResults,
                    {
                      tool: sse.tool,
                      result: sse.result,
                      timestamp: Date.now(),
                    },
                  ],
                  toolCallStates: next,
                };
              });
              break;

            case 'tool_error':
              // Phase 4 Session 2: append the structured tool failure onto the
              // current message and clear the active-tool-call spinner. The
              // agent CONTINUES streaming after a tool_error (distinct from the
              // stream-terminating `error` variant) so we do NOT change status
              // or isStreaming.
              //
              // Phase 2 Session 1: ALSO transition the matching toolCallStates
              // entry to phase 'errored' so Session 4's receipt-grouping
              // iteration routes to <ToolErrorCallout> via the dispatcher.
              // I3 invariant: if a plan step is active, mark it errored without
              // auto-clearing — the visual evidence persists across step
              // transitions.
              lastToolRef.current = sse.tool;
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const matched = findLastEntry(
                  prev.toolCallStates,
                  (s): s is Extract<ToolCallState, { phase: 'executing' }> =>
                    isExecuting(s) && s.tool === sse.tool
                );
                const next = new Map(prev.toolCallStates);
                if (matched) {
                  next.set(matched.id, {
                    phase: 'errored',
                    tool: matched.state.tool,
                    input: matched.state.input,
                    error: {
                      code: sse.code,
                      message: sse.message,
                      details: sse.details,
                    },
                  });
                } else {
                  const fallbackId = `legacy-live-${sse.tool}-${Date.now()}`;
                  next.set(fallbackId, {
                    phase: 'errored',
                    tool: sse.tool,
                    input: null,
                    error: {
                      code: sse.code,
                      message: sse.message,
                      details: sse.details,
                    },
                  });
                }
                const nextPlan =
                  prev.plan && prev.plan.activeStepId
                    ? { ...prev.plan, erroredStepId: prev.plan.activeStepId }
                    : prev.plan;
                return {
                  ...prev,
                  activeToolCall: deriveActiveToolCall(next),
                  toolErrors: [
                    ...prev.toolErrors,
                    {
                      tool: sse.tool,
                      code: sse.code,
                      message: sse.message,
                      details: sse.details,
                      timestamp: Date.now(),
                    },
                  ],
                  toolCallStates: next,
                  plan: nextPlan,
                };
              });
              break;

            // ---------- Plan lifecycle ----------

            case 'plan_proposed':
              setCurrentMessage((prev) =>
                prev
                  ? {
                      ...prev,
                      plan: {
                        steps: sse.steps,
                        activeStepId: null,
                        completedStepIds: new Set(),
                        erroredStepId: null,
                      },
                    }
                  : prev
              );
              break;

            case 'plan_step_active':
              setCurrentMessage((prev) =>
                prev?.plan
                  ? {
                      ...prev,
                      plan: { ...prev.plan, activeStepId: sse.step_id },
                    }
                  : prev
              );
              break;

            case 'plan_step_complete':
              setCurrentMessage((prev) =>
                prev?.plan
                  ? {
                      ...prev,
                      plan: {
                        ...prev.plan,
                        completedStepIds: new Set([
                          ...prev.plan.completedStepIds,
                          sse.step_id,
                        ]),
                        activeStepId:
                          prev.plan.activeStepId === sse.step_id
                            ? null
                            : prev.plan.activeStepId,
                      },
                    }
                  : prev
              );
              break;

            // ---------- Abort ----------

            case 'aborted':
              setCurrentMessage((prev) => {
                if (!prev) return prev;
                const next = new Map(prev.toolCallStates);
                for (const [id, state] of next) {
                  if (isPendingTerminal(state)) {
                    next.set(id, {
                      phase: 'aborted',
                      tool: state.tool,
                      input: 'input' in state ? state.input : null,
                    });
                  }
                }
                return {
                  ...prev,
                  isStreaming: false,
                  activeToolCall: null,
                  abortedReason: sse.reason,
                  toolCallStates: next,
                };
              });
              setRetryStatus(null);
              setStatus('aborted');
              break;

            // ---------- Summary ----------

            case 'summary':
              setCurrentMessage((prev) =>
                prev
                  ? {
                      ...prev,
                      summary: { sections: sse.sections },
                    }
                  : prev
              );
              break;

            // ---------- Stream terminators ----------

            case 'done':
              setRetryStatus(null);
              setCurrentMessage((prev) =>
                prev
                  ? { ...prev, isStreaming: false, activeToolCall: null }
                  : prev
              );
              setStatus('done');
              // NEW Phase 3 § 3.4 — PostHog product event: agent run completed.
              posthog.capture('run_completed', {
                conversation_id: conversationId,
              });
              // Pull the freshly-persisted messages + agent_state into the cache.
              // refetchQueries (NOT invalidateQueries) for the active conversation
              // so the network round-trip is GUARANTEED here at done — pre-fix used
              // invalidateQueries, which only marked the cache stale and relied on
              // an active observer to re-fetch. Surfaced 2026-05-06.
              queryClient.refetchQueries({
                queryKey: queryKeys.conversations.detail(conversationId),
              });
              queryClient.invalidateQueries({
                queryKey: queryKeys.conversations.list(),
              });
              // Two delayed refetches pick up post-stream out-of-band agent_state
              // writes that fire AFTER the SSE done event — currently just
              // `external-sharing` Phase 6's `geocodeAndCache` hook (fire-and-forget
              // Census Geocoder; writes `agent_state.aerialLatLng`). Latency
              // distribution differs sharply by environment:
              //   - Local dev API: ~1-2s typical (covered by 5s refetch).
              //   - Cloud Run warm: ~3-5s (also covered by 5s).
              //   - Cloud Run cold-start: 60-90s+ (need a longer-tailed refetch).
              // 5s catches the common path; 30s catches the cold-start tail. The
              // proper long-term fix is a server-side Realtime broadcast on the
              // existing conversation channel after geocodeAndCache writes —
              // backlogged as `external-sharing-aerial-realtime-broadcast`. Cost
              // here is two extra GETs per stream that return the same data when
              // no aerial change happened. Surfaced 2026-05-09 during dev smoke.
              setTimeout(() => {
                queryClient.refetchQueries({
                  queryKey: queryKeys.conversations.detail(conversationId),
                });
              }, 5000);
              setTimeout(() => {
                queryClient.refetchQueries({
                  queryKey: queryKeys.conversations.detail(conversationId),
                });
              }, 30000);
              break;

            case 'retry_status':
              // Phase 7 overload retry (2026-05-15). Surface inline "Retrying…"
              // indicator. Cleared on success / final error / abort.
              setRetryStatus({
                attempt: sse.attempt,
                totalAttempts: sse.total_attempts,
                delayMs: sse.delay_ms,
              });
              break;

            case 'error':
              if (sse.code === 'MODEL_VERSION_REMOVED' && sse.details) {
                setMissingModel({
                  modelId: sse.details.modelId ?? '',
                  modelVersion: sse.details.modelVersion ?? '',
                  latestAvailable: sse.details.latestAvailable ?? null,
                });
              }
              if (sse.code === 'PAYMENT_REQUIRED') {
                // signup-billing-mvp Phase 6 Session 3 — push 402 signal into
                // the cross-cutting context so <MonthlyAllowanceBanner> reacts.
                // Reason carries through to the banner's user-facing copy.
                const detailReason =
                  sse.details &&
                  typeof (sse.details as { reason?: unknown }).reason ===
                    'string'
                    ? (sse.details as { reason: string }).reason
                    : 'allowance_exhausted';
                setPaymentRequired({ reason: detailReason });
                // NEW Phase 3 § 3.4 — PostHog product event: billing gate hit.
                posthog.capture('payment_required_hit', {
                  conversation_id: conversationId,
                });
              }
              setRetryStatus(null);
              // Phase 7 (2026-05-15) — retriable error surface. UPSTREAM_OVERLOADED
              // ships with `retriable: true`; we store the original send-args so
              // the UI's Retry button can re-fire without making the user re-type.
              if (sse.retriable === true && lastSendRef.current) {
                setRetriableError({
                  message: sse.message,
                  code: sse.code,
                  conversationId: lastSendRef.current.conversationId,
                  content: lastSendRef.current.content,
                });
                // Don't set the generic `error` field — the retry card replaces
                // the generic banner in <ChatThread>.
              } else {
                setError(sse.message);
              }
              setCurrentMessage((prev) =>
                prev
                  ? { ...prev, isStreaming: false, activeToolCall: null }
                  : prev
              );
              setStatus('error');
              break;

            case 'security_block':
              setSecurityBlock({
                reason: sse.reason,
                signals: sse.signals,
              });
              setCurrentMessage((prev) =>
                prev
                  ? { ...prev, isStreaming: false, activeToolCall: null }
                  : prev
              );
              setStatus('security_blocked');
              break;

            case 'security_redact':
              setCurrentMessage((prev) =>
                prev
                  ? {
                      ...prev,
                      redactedSpans: [
                        ...prev.redactedSpans,
                        {
                          category: sse.category,
                          stage: sse.stage,
                          redactedSpan: sse.redacted_span,
                          replacement: sse.replacement,
                          timestamp: Date.now(),
                        },
                      ],
                    }
                  : prev
              );
              break;

            // ---------- Protocol version break ----------

            case 'protocol_changed':
              // builder-ssot-consolidation Phase 5 Session 5.8 (ADR 0019 Decision A).
              // Server emits this when the client's protocol version is outdated.
              // Trigger a once-per-tab reload so the browser picks up the new bundle.
              // Stream closes server-side after this event (ADR 0003).
              triggerProtocolReload();
              return;
          }
        },
        (err) => {
          // NEW Phase 3 § 3.1 — capture SSE stream errors.
          // Each occurrence is an isolated stream failure (not a storm) — NO captureRateLimited.
          Sentry.captureException(err, { tags: { surface: 'sse-stream' } });
          setError(err.message);
          setStatus('error');
          setCurrentMessage((prev) =>
            prev ? { ...prev, isStreaming: false } : prev
          );
        }
      );

      cancelRef.current = cleanup;
    },
    [queryClient, setPaymentRequired]
  );

  const abort = useCallback(async () => {
    // 1. Local — terminate the SSE fetch (releases the HTTP connection).
    cancelRef.current?.();
    cancelRef.current = null;
    // 2. Server-side — terminate the LangGraph run via the Phase 1 endpoint.
    //    Best-effort; 404 is benign (run already finished).
    const conversationId = activeConversationIdRef.current;
    if (conversationId) {
      try {
        await api.abortStream(conversationId, 'user');
      } catch (err) {
        // Connection already torn down by step 1; logging only.
        console.warn('[streaming] server-side abort failed', err);
      }
    }
    // 3. Optimistic UI — directive §10.5 500ms budget. Even if the server's
    //    `aborted` SSE event is in flight or suppressed by a race, force
    //    terminal state so the user sees an immediate response.
    // NEW Phase 3 § 3.4 — PostHog product event: user-initiated abort.
    posthog.capture('run_aborted', {
      conversation_id: conversationId ?? undefined,
    });
    setStatus('aborted');
    setCurrentMessage((prev) =>
      prev
        ? {
            ...prev,
            isStreaming: false,
            activeToolCall: null,
            abortedReason: 'user',
          }
        : prev
    );
  }, []);

  // Phase 7 overload retry (2026-05-15). Re-fires the last sendMessage with
  // the captured (conversationId, content) so the user can retry from a
  // single Retry button click without re-typing. Clears the retriable-error
  // surface first so the card unmounts before the new stream starts.
  const retry = useCallback(() => {
    if (!retriableError) return;
    const { conversationId, content } = retriableError;
    setRetriableError(null);
    void sendMessage(conversationId, content);
  }, [retriableError, sendMessage]);

  const clearError = useCallback(() => {
    setError(null);
    // Reset a terminal error status so the composer re-enables; leave non-error
    // statuses (idle/streaming/aborted/done) untouched.
    setStatus((s) => (s === 'error' ? 'idle' : s));
  }, []);

  return {
    status,
    currentMessage,
    error,
    missingModel,
    securityBlock,
    retryStatus,
    retriableError,
    retry,
    sendMessage,
    abort,
    clearError,
  };
}
